import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ALL_OPTION } from 'ui/FormComponents/FormMultiSelect';
import { DEFAULT_FREQUENCY } from '../NotificationFrequency/NotificationFrequencyOptions';
import {
  useAddNotificationMutation,
  useEditNotificationMutation,
} from 'queries/notifications/queries';
import { t } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalNotification } from 'hooks';

type NotificationsFormValues = {
  id?: string;
  assets: string[];
  description: string;
  categories: string[];
  alarms: {
    selected: boolean;
    models: string[];
    categories: string[];
    frequencies: string[];
  };
};
const defaultValues: NotificationsFormValues = {
  assets: [],
  description: '',
  categories: [ALL_OPTION],
  alarms: {
    selected: false,
    models: [ALL_OPTION],
    categories: [ALL_OPTION],
    frequencies: DEFAULT_FREQUENCY,
  },
};

export function useNotificationsForm(initialValues?: Partial<NotificationsFormValues>) {
  const formMethods = useForm<NotificationsFormValues>({
    defaultValues: {
      ...defaultValues,
      ...initialValues,
    },
  });
  const queryClient = useQueryClient();
  const { displaySnackbar } = useGlobalNotification();
  const onSuccess = () => {
    queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'NOTIFICATIONS' });
    displaySnackbar({ type: 'success', message: t('messages:edit.message') });
  };
  const onError = () => {
    displaySnackbar({ type: 'error', message: t('messages:form_error.message') });
  };
  const { mutate: addNotification, isLoading: isLoadingAdd } = useAddNotificationMutation(
    onSuccess,
    onError,
  );
  const { mutate: editNotification, isLoading: isLoadingEdit } = useEditNotificationMutation(
    onSuccess,
    onError,
  );

  const onSubmit = (data: NotificationsFormValues) => {
    const formatData = {
      id: data.id,
      description: data.description,
      assets: data.assets,
      subjects: [
        {
          notification_type: 'alarms',
          frequencies: data.alarms.frequencies,
          categories: data.alarms.categories,
          models: data.alarms.models,
        },
      ],
    };
  };

  return {
    formMethods,
    onSubmit: formMethods.handleSubmit(onSubmit),
    isLoading: isLoadingEdit || isLoadingAdd,
  };
}
